import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import DateInput from '../common/DateInput';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { BlockUI } from 'primereact/blockui';
import { Dialog } from 'primereact/dialog';
import { driversApi, tachoApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { addDays, addMonths, fmtDate, startOfToday } from '../../utils/dates';
import { driverLabel } from '../../utils/activities';
import PageHeader, { EmptyState } from '../common/PageHeader';

const MIN = 60000;
const timeOfDay = (d) => d.getHours() * 3600000 + d.getMinutes() * MIN;

/* Broj kartice: 14 znakova identifikacije + 2 indeksa (zamena/obnova). Vozilo nosi 14, u bazi je upisan
   nekad pun (16), nekad 14 -> poređenje po prvih 14, prednost tačnom poklapanju. */
const cardKey = (n) => String(n || '').replace(/\s/g, '').toUpperCase();
const findByCard = (drivers, number) => {
    const key = cardKey(number);
    if (key.length < 14) return drivers.find((d) => d.card && cardKey(d.card.cardNumber) === key);
    const same = drivers.filter((d) => d.card && cardKey(d.card.cardNumber).slice(0, 14) === key.slice(0, 14));
    return same.find((d) => cardKey(d.card.cardNumber) === key) || same[0];
};

/* Uvoz podataka VU (jedinica vozila): čita .ddd vozila i raspoređuje vozače po periodima dana. */
export default function ImportVehicleData({ active }) {
    const alerts = useAlerts();
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [from, setFrom] = useState(addMonths(startOfToday(), -1));
    const [to, setTo] = useState(startOfToday());
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [msg, setMsg] = useState(null);

    const load = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const res = await tachoApi.importVehicle(file, from, to);
            const ids = [...new Set(res.succeeded.map((v) => (v.vehicle.company ? v.vehicle.company.id : null)))];
            const all = await driversApi.all({ companyIds: ids });
            setDrivers(all.drivers);
            /* ponuđeni vozači po broju kartice */
            res.succeeded.forEach((v) => v.activities.forEach((a) => {
                a.driverDrivingPeriods = (a.driverDrivingPeriods || []).map((p) => ({
                    ...p,
                    /* kao stari klijent: ako kartica nije u listi, ostaje vozač koga je vratio server */
                    driver: p.driver && p.driver.card ? findByCard(all.drivers, p.driver.card.cardNumber) || p.driver : p.driver || null,
                }));
            }));
            setResult(res);
        } catch { /* toast */ }
        setBusy(false);
    };

    const cloneResult = (r) => ({
        ...r,
        succeeded: r.succeeded.map((v) => ({ ...v, activities: v.activities.map((a) => ({ ...a, driverDrivingPeriods: [...a.driverDrivingPeriods] })) })),
    });
    const updatePeriod = (vi, ai, pi, patch) => setResult((r) => {
        const next = cloneResult(r);
        next.succeeded[vi].activities[ai].driverDrivingPeriods[pi] = { ...next.succeeded[vi].activities[ai].driverDrivingPeriods[pi], ...patch };
        return next;
    });
    const addPeriod = (vi, ai) => setResult((r) => {
        const next = cloneResult(r);
        const day = next.succeeded[vi].activities[ai].activityDate;
        const s = new Date(day); const e = new Date(day); e.setDate(e.getDate() + 1);
        next.succeeded[vi].activities[ai].driverDrivingPeriods.push({ driver: null, startTime: s, endTime: e, codriver: false });
        return next;
    });
    const removePeriod = (vi, ai, pi) => setResult((r) => {
        const next = cloneResult(r);
        next.succeeded[vi].activities[ai].driverDrivingPeriods.splice(pi, 1);
        return next;
    });

    const fail = (title, text) => setMsg({ title, text });

    const save = async () => {
        if (!result || !result.succeeded.length) return fail('Nema podataka', 'Nijedna aktivnost nije pročitana iz fajla. Nema podataka za snimanje.');
        const byDriver = new Map();
        for (const v of result.succeeded) {
            for (const a of v.activities) {
                const day = fmtDate(a.activityDate);
                const periods = a.driverDrivingPeriods;
                if (!periods.length || periods.some((p) => !p.driver)) {
                    return fail('Nisu unešeni svi vozači', `Nisu unešeni vozači za vozilo '${v.vehicle.licenceNumber}' za dan '${day}'. Proverite da li su unešeni svi vozači!`);
                }
                /* normalizacija: sekunde 0, datum = datum aktivnosti, kraj 00:00 -> +1 dan */
                const norm = periods.map((p) => {
                    const s = new Date(a.activityDate); s.setHours(p.startTime.getHours(), p.startTime.getMinutes(), 0, 0);
                    let e = new Date(a.activityDate); e.setHours(p.endTime.getHours(), p.endTime.getMinutes(), 0, 0);
                    if (e.getHours() === 0 && e.getMinutes() === 0) e = addDays(e, 1);
                    return { ...p, startTime: s, endTime: e };
                }).sort((x, y) => x.startTime - y.startTime);
                let sum = 0;
                for (let i = 0; i < norm.length; i++) {
                    if (norm[i].endTime < norm[i].startTime) return fail('Aktivnosti vozača neispravne', `Kraj aktivnosti ne može biti nakon početka aktivnosti (datum: '${day}').`);
                    if (i > 0 && norm[i - 1].endTime > norm[i].startTime) return fail('Aktivnosti vozača neispravne', `Kraj prethodne aktivnosti ne može biti nakon početka trenutne aktivnosti (datum: '${day}').`);
                    sum += norm[i].endTime - norm[i].startTime;
                }
                if (sum !== 86400000) return fail('Aktivnosti vozača neispravne', `Postoje aktivnosti koje nisu dodeljene vozačima (datum: '${day}').`);

                const source = (a.driver1Activities && a.driver1Activities[0] && a.driver1Activities[0].activityDetails) || [];
                for (const p of norm) {
                    /* aktivnosti vozila koje upadaju u period vozača (tolerancija 1 min) */
                    const ps = timeOfDay(p.startTime), pe = p.endTime - p.startTime + ps;
                    const details = source
                        .map((d) => {
                            const ds = timeOfDay(new Date(d.startTime));
                            const de = ds + (new Date(d.endTime) - new Date(d.startTime));
                            const s = Math.max(ds, ps), e = Math.min(de, pe);
                            return e - s > 0 ? { ...d, id: 0, startTime: new Date(+new Date(a.activityDate) + s), endTime: new Date(+new Date(a.activityDate) + e), totalTime: e - s,
                                activityType: p.codriver ? swapCo(d.activityType) : d.activityType } : null;
                        })
                        .filter(Boolean);
                    const entry = byDriver.get(p.driver.id) || { ...p.driver, dailyDriverActivities: [] };
                    let day0 = entry.dailyDriverActivities.find((x) => x.activityDate.getTime() === new Date(a.activityDate).getTime());
                    if (!day0) { day0 = { activityDate: new Date(a.activityDate), activityDetails: [] }; entry.dailyDriverActivities.push(day0); }
                    day0.activityDetails.push(...details);
                    byDriver.set(p.driver.id, entry);
                }
            }
        }
        setBusy(true);
        try {
            await tachoApi.storeVehicles([...byDriver.values()]);
            alerts.success('Aktivnosti uspešno sačuvane');
        } catch (e) {
            fail('Greška prilikom snimanja aktivnosti', e.message);
            alerts.error('Čuvanje aktivnosti neuspešno');
        }
        setBusy(false);
    };

    const driverOptions = drivers.map((d) => ({ label: driverLabel(d), value: d }));

    return (
        <BlockUI blocked={busy} template={<i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem' }} aria-label="Učitavanje" />}>
            <PageHeader title="Unos aktivnosti za vozila" subtitle="Učitajte .ddd fajl jedinice vozila i rasporedite vozače po periodima dana." />
            <div className="panel">
                <div className="filters">
                    <div className="field grow" style={{ maxWidth: '26rem' }}>
                        <label htmlFor="iv-file">Fajl</label>
                        <input ref={fileRef} type="file" accept=".ddd,.esm" hidden onChange={(e) => { setFile(e.target.files[0] || null); e.target.value = ''; }} />
                        <button id="iv-file" type="button" className={`file-pick${file ? ' has-file' : ''}`} onClick={() => fileRef.current.click()}>
                            <i className={`pi ${file ? 'pi-file' : 'pi-folder-open'}`} aria-hidden="true" />
                            <span>{file ? file.name : 'Fajl nije izabran'}</span>
                        </button>
                    </div>
                    <div className="field date"><label htmlFor="iv-from">Od</label><DateInput inputId="iv-from" value={from} onChange={(e) => setFrom(e.value)} dateFormat="dd.mm.yy" showIcon /></div>
                    <div className="field date"><label htmlFor="iv-to">Do</label><DateInput inputId="iv-to" value={to} onChange={(e) => setTo(e.value)} dateFormat="dd.mm.yy" showIcon /></div>
                    <div className="filters-actions">
                        <Button label="Učitaj podatke" icon="pi pi-refresh" severity="secondary" disabled={!file} onClick={load} />
                    </div>
                </div>
            </div>

            {!result && (
                <div className="panel">
                    <EmptyState icon="pi pi-truck" title="Nema učitanih podataka"
                        text="Izaberite .ddd ili .esm fajl jedinice vozila i period, pa kliknite Učitaj podatke." />
                </div>
            )}

            {result && (
                <div className="panel">
                    {result.failed.length > 0 && (
                        <div className="callout danger" role="alert" style={{ marginBottom: '1.25rem' }}>
                            <i className="pi pi-exclamation-triangle" aria-hidden="true" />
                            <div><b>Problemi:</b><ul>{result.failed.map((m, i) => <li key={i}>{m}</li>)}</ul></div>
                        </div>
                    )}
                    {result.succeeded.map((v, vi) => (
                        <div key={vi} className="vehicle-block">
                            <h3 className="vehicle-title">
                                <i className="pi pi-truck" aria-hidden="true" />
                                Aktivnosti vozila: {v.vehicle.licenceNumber}
                                <span className="muted">{v.vehicle.company ? v.vehicle.company.name : 'Bez kompanije'} ({v.vehicle.identificationNumber})</span>
                            </h3>
                            {v.activities.length === 0 && <EmptyState compact icon="pi pi-calendar" text="Nijedna aktivnost nije pronađena za odabrani period" />}
                            {v.activities.map((a, ai) => (
                                <div key={ai} className="day-block">
                                    <div className="day-head">
                                        <span>{fmtDate(a.activityDate)} <span className="muted" style={{ fontWeight: 400 }}>· Vozači</span></span>
                                        <Button label="Dodaj" icon="pi pi-plus" text size="small" onClick={() => addPeriod(vi, ai)} />
                                    </div>
                                    {a.driverDrivingPeriods.length === 0 && <div className="muted" style={{ fontSize: '.82rem' }}>Nije dodeljen nijedan vozač.</div>}
                                    {a.driverDrivingPeriods.map((p, pi) => (
                                        <div key={pi} className="period-row">
                                            <Dropdown value={p.driver} options={driverOptions} dataKey="id" filter aria-label="Vozač"
                                                placeholder="Izaberite vozača" className={!p.driver ? 'p-invalid' : ''}
                                                onChange={(e) => updatePeriod(vi, ai, pi, { driver: e.value })} />
                                            <DateInput value={p.startTime} timeOnly hourFormat="24" aria-label="Od" onChange={(e) => updatePeriod(vi, ai, pi, { startTime: e.value })} />
                                            <DateInput value={p.endTime} timeOnly hourFormat="24" aria-label="Do" onChange={(e) => updatePeriod(vi, ai, pi, { endTime: e.value })} />
                                            <label className="check"><Checkbox checked={!!p.codriver} onChange={(e) => updatePeriod(vi, ai, pi, { codriver: e.checked })} /> Suvozač</label>
                                            <Button icon="pi pi-trash" rounded text severity="danger" tooltip="Ukloni" aria-label="Ukloni vozača" onClick={() => removePeriod(vi, ai, pi)} />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                    <div className="panel-foot"><Button label="Sačuvaj" icon="pi pi-check" onClick={save} /></div>
                </div>
            )}

            <Dialog visible={!!msg} header={msg && msg.title} onHide={() => setMsg(null)} style={{ width: '32rem' }} modal
                footer={<div className="dialog-footer"><Button label="OK" onClick={() => setMsg(null)} /></div>}>
                <div className="callout danger"><i className="pi pi-exclamation-triangle" aria-hidden="true" /><span>{msg && msg.text}</span></div>
            </Dialog>
        </BlockUI>
    );
}

/* Suvozač: vožnja <-> raspoloživost. */
const swapCo = (t) => (t === 'driving' ? 'availability' : t === 'availability' ? 'driving' : t);
